import { computed, signal } from '@angular/core';
import { catchError, EMPTY, Observable, race, ReplaySubject, switchMap, takeUntil, tap, timer } from 'rxjs';
import { logger } from 'src/app/pbir/utils/logger';
import { ErrorDetails } from 'src/app/services/api.services';
import { VisualDataResource, VisualDataResourceStatus } from '../../VisualDataSource';
import { TableImpl } from './TableImpl';
import { getErrorDetails } from './utils';

const LOGGER = logger('ResourceImpl');

export class ResourceImpl<T> implements VisualDataResource<T> {
  constructor(
    public readonly table: TableImpl,
    public readonly sourceFactory: (aborted?: Observable<void>) => Observable<T>,
    public readonly onAbort?: () => void,
    public readonly debugName?: string,
    public readonly abortSignal?: Observable<void>) {
    this.load();
  }

  readonly abortedSubject = new ReplaySubject<void>(1);

  readonly status = signal<VisualDataResourceStatus>(VisualDataResourceStatus.Pending);

  readonly loadingAt = signal<Date | undefined>(undefined);
  readonly loading = computed(() => this.status() <= VisualDataResourceStatus.Loading);

  readonly completedAt = signal<Date | undefined>(undefined);
  readonly completed = computed(() => this.status() >= VisualDataResourceStatus.Success);
  readonly success = computed(() => this.completed() && this.status() == VisualDataResourceStatus.Success);
  readonly failure = computed(() => this.completed() && this.status() != VisualDataResourceStatus.Success);

  readonly error = signal<ErrorDetails | undefined>(undefined);

  readonly hasResult = signal<boolean>(false);
  readonly result = signal<T | undefined>(undefined);


  _aborted = false;

  abort(): void {
    if (this._aborted)
      return;

    this.fail({
      title: 'Aborted',
      detail: 'The query has been aborted',
    }, true);
  }


  private load(): void {
    let startTime = performance.now();
    LOGGER.debug(`'${this.debugName}': load`, { resource: this, time: startTime });

    let aborted = this.abortSignal
      ? race(this.abortSignal, this.abortedSubject)
      : this.abortedSubject;

    timer(0).pipe(
      switchMap(() => {
        this._completed = false;
        this.loadingAt.set(new Date());
        this.status.set(VisualDataResourceStatus.Loading);

        this.table.resources.loadingResourceCount.update(n => n + 1);

        return this.sourceFactory(aborted).pipe(
          takeUntil(aborted),
          tap(r => {
            let endTime = performance.now();
            LOGGER.debug(
              `'${this.debugName}': load done`, 
              { resource: this, result: r, time: endTime, duration: (endTime - startTime) / 1000 });
            
            this.succeed();

            this.hasResult.set(true);
            this.result.set(r);
          }),
          catchError(err => {
            this.fail(err);
            return EMPTY;
          }),
        );
      }),

      catchError(err => {
        this.fail(err);
        return EMPTY;
      }),
      takeUntil(aborted),
    ).subscribe();
  }


  private _completed = false;

  private complete() {
    if (this._completed)
      return;

    this._completed = true;
    this.completedAt.set(new Date());

    this.table.resources.loadingResourceCount.update(n => n - 1);
  }


  private succeed() {
    LOGGER.debug(`'${this.debugName}': succeed`, { resource: this, complete: true });
    this.status.set(VisualDataResourceStatus.Success);
    this.complete();
  }

  private fail(reason: any, aborted?: boolean) {
    if (!aborted) {
      LOGGER.error(`'${this.debugName}': fail`, { resource: this, reason });
    }

    if (!this._aborted) {
      if (aborted) {
        if (!this._completed) {
          LOGGER.info(`'${this.debugName}': aborted`, { resource: this, complete: false });
        }
      }

      if (this.onAbort) {
        try {
          this.onAbort();
        }
        catch (err) {
          LOGGER.error(`'${this.debugName}': onAbort error`, { resource: this, err });
        }
      }

      this._aborted = true;
      this.abortedSubject.next();
    }

    let error = getErrorDetails(reason);
    this.error.set(error);

    this.status.set(VisualDataResourceStatus.Failure);
    this.complete();
  }
}

